import { useEffect, useState } from 'react';
import { useDataStore } from '../store/dataStore';
import { formatDate, shootTypes, shootStatuses } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Camera,
  Video,
  Plane,
  Search,
  Calendar,
  MapPin,
  Trash2,
  Loader2
} from 'lucide-react';

const shootTypeIcons = {
  photo: Camera,
  video: Video,
  drone: Plane,
  all: Camera
};

export default function ShootsPage() {
  const {
    shoots,
    shootsLoading,
    fetchShoots,
    addShoot,
    updateShootStatus,
    deleteShoot,
    customers,
    fetchCustomers
  } = useDataStore();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    customer_id: '',
    shoot_type: 'photo',
    date: '',
    location: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchShoots();
    fetchCustomers();
  }, [fetchShoots, fetchCustomers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.date) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setIsSubmitting(true);
    try {
      await addShoot(formData);
      toast.success('Çekim eklendi');
      setIsOpen(false);
      setFormData({
        customer_id: '',
        shoot_type: 'photo',
        date: '',
        location: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateShootStatus(id, newStatus);
      toast.success('Durum güncellendi');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu çekimi silmek istediğinize emin misiniz?')) {
      try {
        await deleteShoot(id);
        toast.success('Çekim silindi');
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  const filteredShoots = shoots.filter(s => {
    const matchesSearch = s.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6" data-testid="shoots-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Çekimler</h1>
          <p className="text-muted-foreground mt-1">Tüm çekimlerinizi takip edin</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-shoot-btn">
              <Plus className="h-4 w-4 mr-2" />
              Yeni Çekim
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Yeni Çekim Ekle</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Müşteri *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                >
                  <SelectTrigger data-testid="shoot-customer-select">
                    <SelectValue placeholder="Müşteri seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.bride_name} & {customer.groom_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Çekim Türü *</Label>
                <Select
                  value={formData.shoot_type}
                  onValueChange={(value) => setFormData({ ...formData, shoot_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="photo">Fotoğraf</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="drone">Drone</SelectItem>
                    <SelectItem value="all">Hepsi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tarih *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="shoot-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Konum</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Çekim yeri"
                />
              </div>
              <div className="space-y-2">
                <Label>Notlar</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek notlar..."
                  rows={3}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)} className="flex-1">
                  İptal
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1" data-testid="shoot-submit-btn">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Çekim ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Durum filtrele" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            <SelectItem value="planned">Planlandı</SelectItem>
            <SelectItem value="shooting">Çekimde</SelectItem>
            <SelectItem value="editing">Düzenleniyor</SelectItem>
            <SelectItem value="delivered">Teslim Edildi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Shoots List */}
      {shootsLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredShoots.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredShoots.map((shoot, index) => {
            const ShootIcon = shootTypeIcons[shoot.shoot_type] || Camera;
            return (
              <Card key={shoot.id} className={`animate-fade-in stagger-${(index % 5) + 1}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <ShootIcon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium status-${shoot.status}`}>
                        {shootStatuses[shoot.status]}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(shoot.id)}
                        className="text-destructive hover:text-destructive h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="font-semibold mb-1">{shoot.customer_name}</h3>
                  <p className="text-sm text-primary font-medium mb-3">
                    {shootTypes[shoot.shoot_type]}
                  </p>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(shoot.date)}</span>
                    </div>
                    {shoot.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span>{shoot.location}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <Select
                      value={shoot.status}
                      onValueChange={(value) => handleStatusChange(shoot.id, value)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="planned">Planlandı</SelectItem>
                        <SelectItem value="shooting">Çekimde</SelectItem>
                        <SelectItem value="editing">Düzenleniyor</SelectItem>
                        <SelectItem value="delivered">Teslim Edildi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <Camera className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="text-lg font-medium mb-2">Henüz çekim yok</h3>
          <p className="text-muted-foreground mb-4">Yeni çekim ekleyerek başlayın</p>
        </div>
      )}
    </div>
  );
}
